import { state, toast, log } from './ui.js';

const RULES = [
  'Правило: працюємо лише після QR поштомату',
  'Правило: проблеми фіксуємо одразу, щоб супровід бачив контекст',
  'Правило: комірка відкривається тільки після підтвердження забір/вкладення',
  'Правило: інвентаризацію завершуємо перед переходом до вкладень',
];

const STEP_DEFS = [
  {
    id: 'scan-locker',
    event: 'locker-resolved',
    xp: 60,
    title: 'Скануйте поштомат',
    summary: 'Натисніть «Сканувати» та дочекайтесь, поки у шапці з’явиться назва демо-поштомату.',
    hint: 'Правило №1 — без QR жодні посилки не завантажуються. Натисніть «Сканувати» й підтвердіть поштомат.',
  },
  {
    id: 'inventory',
    event: 'inventory-cleared',
    xp: 80,
    title: 'Закрийте інвентаризацію',
    summary: 'Проставте статуси трьох демо-посилок, щоб панель інвентаризації закрилась автоматично.',
    hint: 'Перед заборами робимо швидку інвентаризацію: оберіть посилки та задайте статус «Посилка знайдена».',
  },
  {
    id: 'pickup',
    event: 'pickup-finished',
    xp: 80,
    title: 'Завершіть забір',
    summary: 'Опрацюйте демо-посилки на забір: підтвердьте їх або зафіксуйте проблему.',
    hint: 'Після відкривання комірки підтвердьте забір. Якщо щось не так — натисніть «Проблема» і додайте коментар.',
  },
  {
    id: 'delivery',
    event: 'delivery-confirmed',
    xp: 80,
    title: 'Вкладіть демо-посилку',
    summary: 'Скануйте посилку на вкладення, підберіть комірку та підтвердьте операцію.',
    hint: 'Оберіть розмір комірки в сітці. Під літерою видно, скільки таких комірок вільно; сірий розмір із підписом «немає» не відкриється — візьміть сусідній.',
    guard(payload) {
      if (!payload || !payload.parcelID) return { ok: true };
      if (String(payload.parcelID).startsWith('DLV-')) return { ok: true };
      return { ok: false, message: 'Для тренажера оберіть одну з демо-посилок на вкладення.' };
    },
  },
  {
    id: 'wrap',
    event: 'delivery-finished',
    xp: 40,
    title: 'Підсумуйте сесію',
    summary: 'Коли залишиться 0 посилок на вкладення, тренажер покаже фінальний результат.',
    hint: 'Після останнього вкладення перегляньте фінальний екран. Це сигнал, що сесію можна завершувати.',
  },
];

const dom = {
  panel: null,
  bar: null,
  barLabel: null,
  barStep: null,
  barHint: null,
  hint: null,
  stepsHost: null,
  score: null,
  progressText: null,
  progressBar: null,
  rule: null,
  exit: null,
  restart: null,
};

const internal = {
  initialised: false,
  active: false,
  stepIndex: 0,
  xp: 0,
  mistakes: 0,
  steps: [],
  nodes: new Map(),
  ruleIndex: 0,
  completed: false,
  panelOpen: false,
};

function ensureInit() {
  if (internal.initialised) return true;
  dom.panel = document.getElementById('simulator_panel');
  dom.bar = document.getElementById('simulator_bar');
  dom.barLabel = dom.bar?.querySelector('.sim-header-bar-label') || null;
  dom.barStep = document.getElementById('simulator_bar_step');
  dom.barHint = document.getElementById('simulator_bar_hint');
  dom.hint = document.getElementById('simulator_current_hint');
  dom.stepsHost = document.getElementById('simulator_steps');
  dom.score = document.getElementById('simulator_score');
  dom.progressText = document.getElementById('simulator_progress_text');
  dom.progressBar = document.getElementById('simulator_progress_bar');
  dom.rule = document.getElementById('simulator_rule');
  dom.exit = document.getElementById('simulator_exit');
  dom.restart = document.getElementById('simulator_restart');
  internal.initialised = Boolean(
    dom.panel &&
    dom.bar &&
    dom.barStep &&
    dom.barHint &&
    dom.hint &&
    dom.stepsHost &&
    dom.score &&
    dom.progressText &&
    dom.progressBar
  );
  if (!internal.initialised) {
    log('simulator:init_failed');
    return false;
  }
  dom.exit?.addEventListener('click', () => stop({ userInitiated: true }));
  dom.restart?.addEventListener('click', () => start({ restart: true }));
  dom.bar?.addEventListener('click', () => setPanelOpen(!internal.panelOpen));
  return true;
}

function setPanelOpen(open) {
  internal.panelOpen = !!open;
  if (dom.panel) dom.panel.classList.toggle('hidden', !internal.panelOpen);
  if (dom.bar) dom.bar.setAttribute('aria-expanded', internal.panelOpen ? 'true' : 'false');
}

function cloneSteps() {
  internal.steps = STEP_DEFS.map((step) => ({ ...step, status: 'pending' }));
  internal.nodes.clear();
  dom.stepsHost.innerHTML = '';
  internal.steps.forEach((step, idx) => {
    const li = document.createElement('li');
    li.className = 'sim-step';
    li.dataset.stepId = step.id;
    li.setAttribute('data-step-index', String(idx + 1));
    li.innerHTML = `<div class="sim-step-title">${step.title}</div><p class="sim-step-summary">${step.summary}</p>`;
    dom.stepsHost.appendChild(li);
    internal.nodes.set(step.id, li);
  });
}

function updateStepVisual(step, status) {
  const node = internal.nodes.get(step.id);
  if (!node) return;
  node.classList.remove('active', 'completed', 'failed');
  if (status === 'active') node.classList.add('active');
  else if (status === 'completed') node.classList.add('completed');
  else if (status === 'failed') node.classList.add('failed');
}

function updateHint(text) {
  if (dom.hint) dom.hint.textContent = text || '';
  if (dom.barHint) dom.barHint.textContent = text || '';
}

function updateScore() {
  if (!dom.score) return;
  dom.score.textContent = `Досвід: ${internal.xp} · Помилки: ${internal.mistakes}`;
}

function updateProgress() {
  if (!dom.progressText || !dom.progressBar) return;
  const total = internal.steps.length;
  const current = Math.min(internal.stepIndex + 1, total);
  dom.progressText.textContent = `Крок ${Math.min(current, total)} з ${total}`;
  if (dom.barStep) dom.barStep.textContent = `Крок ${Math.min(current, total)} з ${total}`;
  const width = Math.min((internal.stepIndex / total) * 100, 100);
  dom.progressBar.style.width = `${width}%`;
}

function pickRule() {
  internal.ruleIndex = Math.floor(Math.random() * RULES.length);
  if (dom.rule) dom.rule.textContent = RULES[internal.ruleIndex];
}

function resetState() {
  internal.stepIndex = 0;
  internal.xp = 0;
  internal.mistakes = 0;
  internal.completed = false;
  cloneSteps();
  internal.steps.forEach((step, idx) => {
    updateStepVisual(step, idx === 0 ? 'active' : 'pending');
  });
  if (dom.barLabel) dom.barLabel.textContent = 'Навчання активне';
  updateHint(internal.steps[0]?.hint || '');
  updateScore();
  updateProgress();
  pickRule();
}

function revealUI() {
  dom.bar?.classList.remove('hidden');
  setPanelOpen(false);
}

function hideUI() {
  dom.bar?.classList.add('hidden');
  setPanelOpen(false);
}

function finishTraining() {
  internal.completed = true;
  updateScore();
  updateProgress();
  if (dom.rule) dom.rule.textContent = 'Навчання завершено';
  updateHint('Тренажер пройдено! Можна повторити або повернутися до реальних операцій.');
  if (dom.barLabel) dom.barLabel.textContent = 'Навчання завершено';
  if (dom.barStep) dom.barStep.textContent = 'Завершено';
}

function registerMistake(message) {
  internal.mistakes += 1;
  updateScore();
  if (message) toast(message);
}

function advance(payload) {
  const step = internal.steps[internal.stepIndex];
  if (!step) return;
  step.status = 'completed';
  updateStepVisual(step, 'completed');
  internal.xp += step.xp || 0;
  internal.stepIndex += 1;
  updateScore();
  updateProgress();
  if (internal.stepIndex >= internal.steps.length) {
    finishTraining();
    return;
  }
  const next = internal.steps[internal.stepIndex];
  updateStepVisual(next, 'active');
  updateHint(next.hint);
  if (payload && payload.notice) toast(payload.notice);
}

function handleEvent(event, payload) {
  if (!internal.active) return;
  if (!ensureInit()) return;
  if (!event) return;
  const step = internal.steps[internal.stepIndex];
  if (!step) return;
  const targetIdx = internal.steps.findIndex((item) => item.event === event);
  if (targetIdx === -1) return;
  if (targetIdx > internal.stepIndex) {
    registerMistake(`Спочатку виконайте крок «${step.title}».`);
    updateStepVisual(step, 'active');
    return;
  }
  if (targetIdx < internal.stepIndex) {
    // повторна дія — не нараховуємо бали, але і не караємо
    if (event === 'session-finished' && internal.completed) {
      updateHint('Сесію закрито. Тренажер можна перезапустити для повторення.');
    }
    return;
  }
  if (typeof step.guard === 'function') {
    try {
      const check = step.guard(payload);
      if (check && typeof check === 'object' && check.ok === false) {
        registerMistake(check.message || 'Спробуйте іншу дію для цього кроку.');
        updateStepVisual(step, 'active');
        return;
      }
      if (check === false) {
        registerMistake('Переходимо до наступного кроку лише після правильної дії.');
        updateStepVisual(step, 'active');
        return;
      }
    } catch (err) {
      log('simulator:guard_error ' + (err?.message || err));
    }
  }
  advance(payload || {});
}

function start({ restart = false } = {}) {
  if (!ensureInit()) return;
  internal.active = true;
  resetState();
  revealUI();
  if (!restart) toast('Тренажер кур’єра увімкнено. Дотримуйтеся кроків праворуч.');
  log('simulator:start', { restart });
}

function stop({ userInitiated = false } = {}) {
  if (!internal.active) return;
  internal.active = false;
  hideUI();
  const defaultHint = STEP_DEFS[0]?.hint || '';
  const defaultStep = `Крок 1 з ${STEP_DEFS.length}`;
  if (dom.barLabel) dom.barLabel.textContent = 'Навчання активне';
  if (dom.barStep) dom.barStep.textContent = defaultStep;
  if (dom.barHint) dom.barHint.textContent = defaultHint;
  if (userInitiated) toast('Тренажер згорнуто. Ви можете продовжити роботу у демо-режимі.');
  log('simulator:stop', { userInitiated });
}

function isActive() {
  return internal.active;
}

export function initSimulator() {
  ensureInit();
}

export const simulator = {
  start,
  stop,
  notify: handleEvent,
  mistake: registerMistake,
  isActive,
};
